import { Component } from '@theme/component';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

/**
 * wld-header — keeps Horizon's header contract without header.js:
 * - writes `--header-height` (this element) and `--header-group-height` (every #header-group child)
 *   on <body>, so heroes, sticky product media and the account dialog keep their offsets;
 * - writes `--wld-header-bar-height` (the bar alone) on itself: the open mobile menu is capped by
 *   it, not by `--header-height`, which includes the menu and would feed back into its own cap;
 * - toggles the mobile menu (a disclosure: aria-expanded on the button, `data-menu-open` on the root);
 * - toggles the mega menu panel (a disclosure: the item stays a link, its button opens the panel; on
 *   desktop a mouse hovering the item opens it too; Escape, a click outside or focus leaving the
 *   item closes it);
 * - keeps `aria-expanded` on the account button in step with `#account-drawer` (wld-account-drawer),
 *   as header-actions does for the cart drawer.
 */
class WldHeader extends Component {
  requiredRefs = ['bar'];

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {MediaQueryList | undefined} */
  #desktopQuery;

  /** @type {number | undefined} */
  #megaCloseTimer;

  /** @type {HTMLElement[]} Elements made inert while the menu is open, restored on close */
  #inerted = [];

  connectedCallback() {
    super.connectedCallback();

    this.#resizeObserver = new ResizeObserver(() => this.#updateHeights());
    this.#resizeObserver.observe(this);
    const group = document.querySelector('#header-group');
    if (group) {
      for (const element of group.children) this.#resizeObserver.observe(element);
    }
    this.#updateHeights();

    this.#desktopQuery = window.matchMedia('(min-width: 990px)');
    this.#desktopQuery.addEventListener('change', this.#onBreakpointChange);
    this.addEventListener('keydown', this.#onKeydown);
    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.addEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);

    const megaItem = this.#megaItem;
    if (megaItem) {
      megaItem.addEventListener('pointerenter', this.#onMegaPointerEnter);
      megaItem.addEventListener('pointerleave', this.#onMegaPointerLeave);
      megaItem.addEventListener('focusout', this.#onMegaFocusOut);
      document.addEventListener('click', this.#onDocumentClick);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    this.#desktopQuery?.removeEventListener('change', this.#onBreakpointChange);
    this.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.removeEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);

    const megaItem = this.#megaItem;
    megaItem?.removeEventListener('pointerenter', this.#onMegaPointerEnter);
    megaItem?.removeEventListener('pointerleave', this.#onMegaPointerLeave);
    megaItem?.removeEventListener('focusout', this.#onMegaFocusOut);
    document.removeEventListener('click', this.#onDocumentClick);
    window.clearTimeout(this.#megaCloseTimer);
    for (const element of this.#inerted) element.inert = false;
    this.#inerted = [];
  }

  /** @returns {HTMLElement | null} */
  get #megaItem() {
    const { megaButton } = this.refs;
    return megaButton instanceof HTMLElement ? megaButton.closest('.wld-header__menu-item') : null;
  }

  toggleMega() {
    const { megaButton } = this.refs;
    this.#setMega(megaButton?.getAttribute('aria-expanded') !== 'true');
  }

  /** @param {boolean} open */
  #setMega(open) {
    const { megaButton, mega } = this.refs;
    if (!(megaButton instanceof HTMLElement) || !(mega instanceof HTMLElement)) return;
    window.clearTimeout(this.#megaCloseTimer);
    megaButton.setAttribute('aria-expanded', String(open));
    mega.hidden = !open;
  }

  /** @param {PointerEvent} event */
  #onMegaPointerEnter = (event) => {
    if (event.pointerType === 'mouse' && this.#desktopQuery?.matches) this.#setMega(true);
  };

  /** @param {PointerEvent} event */
  #onMegaPointerLeave = (event) => {
    if (event.pointerType !== 'mouse' || !this.#desktopQuery?.matches) return;
    window.clearTimeout(this.#megaCloseTimer);
    this.#megaCloseTimer = window.setTimeout(() => this.#setMega(false), 200);
  };

  /** @param {FocusEvent} event */
  #onMegaFocusOut = (event) => {
    const item = this.#megaItem;
    if (item && event.relatedTarget instanceof Node && !item.contains(event.relatedTarget)) this.#setMega(false);
  };

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    const item = this.#megaItem;
    if (item && event.target instanceof Node && !item.contains(event.target)) this.#setMega(false);
  };

  /** @param {Event} event */
  #onDrawerStateChange = (event) => {
    const drawer = /** @type {HTMLElement | null} */ (event.target);
    if (drawer?.id !== 'account-drawer') return;
    const trigger = this.querySelector('[aria-controls="account-drawer"]');
    trigger?.setAttribute('aria-expanded', drawer.hasAttribute('open') ? 'true' : 'false');
    if (drawer.hasAttribute('open')) this.#setMenu(false);
  };

  toggleMenu() {
    const open = !this.hasAttribute('data-menu-open');
    this.#setMenu(open);
    const { menuButton, navClose } = this.refs;
    const target = open ? navClose : menuButton;
    if (target instanceof HTMLElement) target.focus();
  }

  /**
   * The open menu covers the whole screen: everything outside it — the bar, the rest of the header
   * group, the page — is inert, so focus and screen readers stay in the menu.
   * @param {boolean} open
   */
  #setMenu(open) {
    const { menuButton, bar } = this.refs;
    if (!(menuButton instanceof HTMLElement)) return;
    if (open === this.hasAttribute('data-menu-open')) return;
    this.toggleAttribute('data-menu-open', open);
    menuButton.setAttribute('aria-expanded', String(open));

    if (bar instanceof HTMLElement) bar.inert = open;

    // Siblings of every ancestor up to <body> — never an ancestor itself (the header sits inside
    // `.page-wrapper`; making that inert would make the menu inert too).
    for (const element of this.#inerted) element.inert = false;
    this.#inerted = [];
    if (!open) return;
    for (let node = /** @type {Element} */ (this); node.parentElement && node !== document.body; node = node.parentElement) {
      for (const sibling of node.parentElement.children) {
        if (sibling === node || !(sibling instanceof HTMLElement) || sibling.inert) continue;
        if (sibling instanceof HTMLScriptElement || sibling instanceof HTMLStyleElement) continue;
        // Shopify's cookie consent banner and its preferences panel sit on top of the menu and must
        // stay clickable — as a direct child or inside a wrapper.
        if (sibling.id.startsWith('shopify-pc__') || sibling.querySelector('[id^="shopify-pc__"]')) continue;
        sibling.inert = true;
        this.#inerted.push(sibling);
      }
    }
  }

  #onBreakpointChange = () => {
    if (this.#desktopQuery?.matches) this.#setMenu(false);
    this.#setMega(false);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const { megaButton } = this.refs;
    if (megaButton instanceof HTMLElement && megaButton.getAttribute('aria-expanded') === 'true') {
      this.#setMega(false);
      megaButton.focus();
      return;
    }
    if (!this.hasAttribute('data-menu-open')) return;
    this.#setMenu(false);
    const { menuButton } = this.refs;
    if (menuButton instanceof HTMLElement) menuButton.focus();
  };

  #updateHeights() {
    let groupHeight = 0;
    const group = document.querySelector('#header-group');
    if (group) {
      for (const element of group.children) {
        if (element instanceof HTMLElement) groupHeight += element.offsetHeight;
      }
    }

    const { bar } = this.refs;
    if (bar instanceof HTMLElement) this.style.setProperty('--wld-header-bar-height', `${Math.round(bar.offsetHeight)}px`);
    const headerHeight = `${Math.round(this.offsetHeight)}px`;
    document.body.style.setProperty('--header-height', headerHeight);
    document.body.style.setProperty('--header-group-height', `${Math.round(groupHeight)}px`);
    // Also on <html>: its scroll-padding-top reads it there, and <html> does not inherit from <body>
    document.documentElement.style.setProperty('--header-height', headerHeight);
  }
}

if (!customElements.get('wld-header')) {
  customElements.define('wld-header', WldHeader);
}
